import { z } from 'zod';
import { sessionSideSchema, versionPinSchema } from './versioning.js';

/**
 * The ENTRY LINK: the door an operator distributes (refonte modulaire §5.1,
 * §5.3, §11.4; issue #59).
 *
 * A name for the operator, an undiscoverable secret in the URL, an author, a
 * date, a list of entries (experience, pin, side), a lifetime taken from a closed
 * list, a revocation. Nothing else - and in particular NOT a version: a link is
 * not a versioned object (§4), it has no draft, no pointer and no history, so
 * none of `versioning/` applies to it. What it shares with the four versioned
 * kinds is one thing only: an ACTIVE link counts as a usage of the experiences
 * it plays (§4, H25), which is why `usageLineSchema` in `versioning.ts` carries
 * a `link` kind.
 *
 * ## What is stored, and what is derived
 *
 * §5.1 asks for an expiry « dérivée à la lecture, sans aucune tâche planifiée »,
 * at the régime the session applies to its 24 hours (§5.3) and the chat to its
 * window. Two fields are therefore stored - the `lifetime` chosen from the
 * closed list, and the instant that lifetime counts FROM - and `expiresAt` is
 * computed on every read, the way `sessionSummarySchema` derives its own from
 * `startedAt`. The anchor and not a stored `expiresAt`, because everything here
 * is editable after the fact (§5.1): a stored expiry would have to be rewritten
 * on a lifetime change, and a save of the name alone that sent the lifetime
 * back unchanged would have silently undone an extension.
 *
 * `state` is derived the same way, from the same two facts plus `revokedAt`,
 * and is never stored: nothing in the server notices the clock crossing an
 * expiry.
 *
 * ## The secret is the identity
 *
 * « Tout est éditable après coup sauf le secret » (§5.1). It is struck server
 * side at the creation and never accepted from a body, never rewritten, and
 * never logged - `middleware/request-logger.ts` masks the `/api/l/` segment for
 * exactly that reason.
 */

/* ------------------------------------------------------------- the lifetime */

/**
 * The closed list of §5.1, and the identifiers are code - the French (« 24 h »,
 * « 7 jours ») is the screen's.
 *
 * A closed list rather than a date picker: « c'est le geste qu'on fait
 * réellement, et il ne demande aucune arithmétique de calendrier ».
 */
export const linkLifetimeSchema = z.enum(['24h', '48h', '7d', '30d']);
export type LinkLifetime = z.infer<typeof linkLifetimeSchema>;
export const LINK_LIFETIMES = linkLifetimeSchema.options;

/**
 * How long each one lasts, in HOURS and in `shared`, at the régime of
 * `SESSION_TTL_HOURS` and `CHAT_LINK_TTL_HOURS`: a screen says the number out
 * loud, and the backend derives its milliseconds beside the function that
 * computes an expiry.
 */
export const LINK_LIFETIME_HOURS: Readonly<Record<LinkLifetime, number>> = {
  '24h': 24,
  '48h': 48,
  '7d': 24 * 7,
  '30d': 24 * 30,
};

/** The default a fresh creation form opens on - the shortest, deliberately. */
export const LINK_DEFAULT_LIFETIME: LinkLifetime = '24h';

/* ----------------------------------------------------------------- the name */

/** The bound of §11.4's name field (H23 régime): a bound of INPUT, not of meaning. */
export const LINK_NAME_MAX = 80;

/**
 * The maximum number of entries one link carries.
 *
 * A bound of input like every other in this repo, and generous: the choice page
 * of §5.1 is a vertical stack of cards, and past a dozen it stops being a
 * choice. Nothing refuses a link at ten, the number is here so a malformed body
 * cannot ask the base to walk an unbounded list.
 */
export const LINK_ENTRIES_MAX = 20;

/* -------------------------------------------------------------- the entries */

/**
 * One entry (vitrine.md §3.4, V5, V18): the experience, the PIN - `latest`, or a
 * precise version number - and the SIDE the session will play on, `dev` by
 * default.
 *
 * `latest` is « latest dev » on the `dev` side and « latest prod » on the `prod`
 * one (§3.2): on the atelier an entry chooses its side, so a link of the dev can
 * replay exactly what the vitrine plays. On the vitrine an entry is always
 * `latest`, and its side is the only one there is - the store forces both.
 *
 * Not a `versionedRefSchema`, although the shape is close: a ref lives INSIDE a
 * content and its field name says what it points at, where an entry is a row of
 * a form and has to name its own kind - `experienceId` spelled long is what keeps
 * a reader from taking it for the link's own id.
 */
export const pinnedLinkEntrySchema = z
  .object({
    experienceId: z.string().min(1),
    pin: versionPinSchema,
    side: sessionSideSchema.default('dev'),
  })
  .strict();
export type PinnedLinkEntry = z.infer<typeof pinnedLinkEntrySchema>;

/** The name every reader of a link already spells; the entry replaced `{ experienceId, tag }`. */
export const linkEntrySchema = pinnedLinkEntrySchema;
export type LinkEntry = PinnedLinkEntry;

/** At least one - « un lien qui ne peut faire entrer personne ne sert à rien » (§5.1). */
export const linkEntriesSchema = z.array(linkEntrySchema).min(1).max(LINK_ENTRIES_MAX);

/**
 * One entry as the OPERATOR's list reads it (§11.4): the couple, plus what only
 * the base knows about it.
 *
 * `experienceName` is null when the base no longer holds the id at all, and the
 * pastille then says so rather than showing an identifier. `retired` is the
 * case §5.1 names out loud - « une entrée dont l'expérience est retirée après
 * coup disparaît de la page de choix » - so the operator's list is the one
 * place it is still visible.
 */
export const linkEntryViewSchema = linkEntrySchema
  .extend({
    experienceName: z.string().min(1).nullable(),
    retired: z.boolean(),
  })
  .strict();
export type LinkEntryView = z.infer<typeof linkEntryViewSchema>;

/* ------------------------------------------------------------------ the state */

/**
 * The three states of §5.1, and only `revoked` is stored (as a date).
 *
 * `expired` is derived from the clock at every read, `active` is « neither of
 * the two ». A revoked link that later passes its expiry stays `revoked`: it is
 * the gesture that closed it, and the date beside it in the list is the date of
 * that gesture.
 */
export const linkStateSchema = z.enum(['active', 'revoked', 'expired']);
export type LinkState = z.infer<typeof linkStateSchema>;

/* ---------------------------------------------------------------- the summary */

/**
 * One link as Réglages > Partage reads it (§11.4).
 *
 * `secret` IS served here, and that is deliberate and confined: this shape
 * comes out of `/api/links`, which is behind the operator door, and the screen's
 * whole job is to hand the URL over (« l'URL secrète en lecture avec Copier
 * l'URL »). It never travels anywhere else - the public resolution of `/api/l/`
 * answers `linkOfferSchema`, which carries no secret and no name.
 *
 * `sessionCount` is « le nombre de sessions entrées par ce lien » (§11.4),
 * counted on `sessions.link_id` at the read and stored nowhere: a running total
 * on the row would be a second source of truth for a number the sessions
 * already hold - the argument `sessionSummarySchema` makes about `turnCount`.
 */
export const linkSummarySchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    /** The undiscoverable half of `<origine>/l/<secret>` (§5.1). */
    secret: z.string().min(1),
    state: linkStateSchema,
    lifetime: linkLifetimeSchema,
    /** Derived, never stored - see the head of this file. */
    expiresAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    createdBy: z.string().min(1),
    /** When « Révoquer » was pressed; null on every other state, never ''. */
    revokedAt: z.string().datetime().nullable(),
    sessionCount: z.number().int().nonnegative(),
    entries: z.array(linkEntryViewSchema),
  })
  .strict();
export type LinkSummary = z.infer<typeof linkSummarySchema>;

/** The list, served whole - revoked and expired included, greyed in the page (§11.4). */
export const linkListSchema = z.object({ links: z.array(linkSummarySchema) }).strict();
export type LinkList = z.infer<typeof linkListSchema>;

/* --------------------------------------------------------------- the writes */

/**
 * « Créer un lien » (§11.4). The secret is NOT here: it is struck server side,
 * and a body that could choose it would be a body that could choose a link's
 * identity.
 */
export const linkCreateSchema = z
  .object({
    name: z.string().trim().min(1).max(LINK_NAME_MAX),
    lifetime: linkLifetimeSchema,
    entries: linkEntriesSchema,
  })
  .strict();
export type LinkCreate = z.infer<typeof linkCreateSchema>;

/**
 * « Modifier » (§5.1): the name, the lifetime and the entries, all three at
 * once.
 *
 * The same shape as the creation, and one route rather than three: the form is
 * one form, the three fields are saved together, and the refusal of an entry has
 * to be judged on the list as it WOULD be. A changed lifetime re-anchors on the
 * link's current start rather than on today - extending is its own gesture
 * (« Prolonger »), and a save of the name alone must not silently move an
 * expiry.
 */
export const linkUpdateSchema = linkCreateSchema;
export type LinkUpdate = z.infer<typeof linkUpdateSchema>;

/* ------------------------------------------------------------- the refusals */

/**
 * WHY one entry does not resolve (§5.1, strict resolution of §4).
 *
 * Three causes and not one, because the operator's next gesture differs: an
 * unknown id is a stale form, a retired experience has to be restored or
 * replaced, an empty production pointer (`latest` on the `prod` side) is fixed
 * either by passing the experience to production or by choosing the `dev` side
 * on this very row, a version the experience does not have is a stale pin, and
 * on the vitrine an entry that is not `latest` is refused outright (V5).
 */
export const linkEntryProblemCodeSchema = z.enum([
  'experience-unknown',
  'experience-retired',
  'production-empty',
  'version-unknown',
  'pin-not-latest',
]);
export type LinkEntryProblemCode = z.infer<typeof linkEntryProblemCodeSchema>;

/**
 * One refused row, carrying WHERE it is (§11.4: « sous la ligne fautive »).
 *
 * `index` is the position in the list that was SENT, which is the only thing
 * the form can map back onto a row: two entries can name the same experience,
 * and an identifier alone would underline both.
 */
export const linkEntryProblemSchema = z
  .object({
    index: z.number().int().nonnegative(),
    code: linkEntryProblemCodeSchema,
    experienceId: z.string().min(1),
    /** « en nommant l'expérience » (§5.1); null when the base does not hold it. */
    experienceName: z.string().min(1).nullable(),
  })
  .strict();
export type LinkEntryProblem = z.infer<typeof linkEntryProblemSchema>;

/**
 * The 409 of the link (§5.1), at the régime of every other business refusal of
 * this repo: the CONTRACT's own body, and the screen words each code itself.
 *
 * One code today, and it is still a `code` rather than a bare list: the screen
 * reads codes and never French sentences, and a second refusal of the link (a
 * name uniqueness, say) would land beside it without the form changing shape.
 */
export const linkErrorSchema = z
  .object({
    error: z.string(),
    code: z.literal('entry-unresolved'),
    problems: z.array(linkEntryProblemSchema).min(1),
  })
  .strict();
export type LinkError = z.infer<typeof linkErrorSchema>;

/* ------------------------------------------------- the public resolution */

/**
 * One entry as the PERSON sees it on the choice page (§5.1, planche
 * `PageDeChoix`): the name and the short description of the experience, read
 * off the version the entry RESOLVES to.
 *
 * Off the resolved version and not off the entity, because that is the content
 * a session will freeze and play (§5.2): an entry on the `prod` side shows the
 * production wording even while a draft renames the experience.
 *
 * Neither the pin nor the side travels: the person « ne choisit pas une
 * version » (planche), and the POST that starts the session names the
 * experience alone.
 */
export const linkEntryOfferSchema = z
  .object({
    experienceId: z.string().min(1),
    name: z.string().min(1),
    description: z.string(),
  })
  .strict();
export type LinkEntryOffer = z.infer<typeof linkEntryOfferSchema>;

/**
 * What `GET /api/l/<secret>` answers - and it answers the SAME thing for an
 * unknown secret and for a dead link.
 *
 * « Pas d'oracle » is the whole shape of this schema: an unknown secret, a
 * revoked link, an expired one and a link whose every entry stopped resolving
 * all come back as `{ state: 'dead', entries: [] }`, with a 200. A 404 on the
 * unknown one would have made this route an enumerator: try a secret, read the
 * status, learn whether it exists.
 *
 * The link's NAME is absent on purpose (§11.4: « pour l'opérateur, jamais
 * montré à la personne »), and so is its expiry - a date is a fact about the
 * link, and the person is not shown facts about a door she cannot open.
 */
export const linkOfferStateSchema = z.enum(['open', 'dead']);
export type LinkOfferState = z.infer<typeof linkOfferStateSchema>;

export const linkOfferSchema = z
  .object({
    state: linkOfferStateSchema,
    /** Empty on `dead`, and at least one on `open` by construction. */
    entries: z.array(linkEntryOfferSchema),
  })
  .strict();
export type LinkOffer = z.infer<typeof linkOfferSchema>;

/** The one answer a dead or unknown secret gets. Built once so it cannot drift. */
export const DEAD_LINK_OFFER: LinkOffer = { state: 'dead', entries: [] };
